<template>
  <section id="albums" :class="{ desktop: $isDesktop }">
    {{ error }}
    <div
      v-observe-visibility="{
        callback: headerVisibilityChanged,
        throttle: 300,
        throttleOptions: {
          leading: 'hidden',
        },
      }"
    />
    <header :class="{ pinned: navigationOffScreen }">
      <button type="button" @click.stop="backToCampsite()">
        <img
          src="@/assets/images/down-rounded.svg"
          width="16"
          height="16"
          class="back"
          alt=""
        />
        <h1>{{ campsite?.name }}</h1>
      </button>
      <img
        v-if="deviceSupportsShare"
        id="sharebutton"
        src="@/assets/images/share.svg"
        width="24"
        height="24"
        alt=""
        @click.stop="shareCampsiteAlbumsLink()"
      />
      <nav ref="navEl">
        <nuxt-link
          :to="{
            name: 'campsite-albums-album',
            params: { ...$route.params, tag: undefined },
          }"
          replace
          :class="{
            selected: selectedTag === 'all',
          }"
        >
          <translate>All</translate> <i>({{ counts.all }})</i>
        </nuxt-link>
        <nuxt-link
          v-if="counts.pitchtypes"
          :to="{
            name: 'campsite-albums-album',
            params: { ...$route.params, tag: 'pitchtypes' },
          }"
          replace
          :class="{ selected: selectedTag === 'pitchtypes' }"
        >
          <translate>Pitches</translate> <i>({{ counts.pitchtypes }})</i>
        </nuxt-link>
        <nuxt-link
          v-if="counts.onsite"
          :to="{
            name: 'campsite-albums-album',
            params: { ...$route.params, tag: 'onsite-accommodation' },
          }"
          replace
          :class="{ selected: selectedTag === 'onsite-accommodation' }"
        >
          <translate>On-site accommodation</translate>
          <i>({{ counts.onsite }})</i>
        </nuxt-link>
        <nuxt-link
          v-for="(key, index) in Object.keys(computedTags.topLevel)"
          :key="index"
          :to="{
            name: 'campsite-albums-album',
            params: { ...$route.params, tag: key },
          }"
          replace
          :class="{ selected: selectedTag === key }"
        >
          {{ idToLabelMap.groups[key] }} <i>({{ counts.topLevel[key] }})</i>
        </nuxt-link>
        <nuxt-link
          v-if="counts.visitor"
          :to="{
            name: 'campsite-albums-album',
            params: { ...$route.params, tag: 'visitor' },
          }"
          replace
          :class="{ selected: selectedTag === 'visitor' }"
        >
          <translate>Reviewers</translate> <i>({{ counts.visitor }})</i>
        </nuxt-link>
        <nuxt-link
          v-if="campsite && campsite.youtube"
          :to="{ name: 'campsite-albums-video' }"
          replace
          :class="{
            selected: selectedTag === 'video',
          }"
        >
          <translate>Video</translate>
        </nuxt-link>
      </nav>
    </header>

    <KeepAlive>
      <NuxtPage
        v-if="computedTags"
        :page-key="(route.params.slug as string) + (route.params.id as string)"
        :photos-by-tag="computedTags"
        :primary-photo="primaryPhoto"
        :id-to-label-map="idToLabelMap"
        :campsite="campsite"
      />
    </KeepAlive>
  </section>
</template>

<script setup lang="ts">
import { useLazyAsyncData, useNuxtApp, useRoute, useRouter } from 'nuxt/app'
import { computed, onMounted, ref, watch } from 'vue'
import { getPrimaryPhotoFromPitchType } from '~/apps/campsite-details/components/getPrimaryPhoto'
import { PhotoLoaderService } from '~/common/photos/photoLoader'
import { getHttpHeaders } from '~/utility/headers'
import isNotAnonAxiosError from '~/utility/isNotAnonAxiosError'
import { isAbortError } from '~/utility/isAbortError'
import { logger } from '../../utility/logger'
import { AjaxCampsiteDetailsRepository } from '../campsite-details/AjaxCampsiteDetailsRepository'
import { tagSorter } from './tagSorter'

const route = useRoute()
const router = useRouter()
const { $routerClient } = useNuxtApp()
const { i18n, t } = useI18n()
const navEl: Ref<HTMLDivElement | null> = ref(null)
const campsiteSlug = computed(() => route.params.slug)

definePageMeta({
  layout: 'basic',
  httpHeaders: (to) => getHttpHeaders({ to }),
})

const { data, error } = useLazyAsyncData(
  `photos-${campsiteSlug.value}`,
  async () => {
    const repository = new AjaxCampsiteDetailsRepository({
      defaults: {
        defaultAdultsCount: useRuntimeConfig().public.defaultAdultsCount,
      },
    })
    const [campsite, photosData] = await Promise.all([
      repository.get(
        route.params.slug,
        route.params.lang || 'en-gb',
        route.query,
      ),
      loadPhotos(route.params.slug),
    ])

    return {
      campsite,
      albums: campsite ? photosData[campsite.id] : [],
    }
  },
)

async function loadPhotos(campsiteId) {
  const photoLoaderService = new PhotoLoaderService()
  try {
    return await photoLoaderService.loadAlbumsPhotos(campsiteId, i18n.locale)
  } catch (e) {
    const err = e as Error
    if (isNotAnonAxiosError(err)) {
      console.error(err, JSON.stringify(err))
    }
  }
  return Promise.resolve([])
}

const deviceSupportsShare = ref(true)

onMounted(() => {
  window.scrollTo(0, 0)
  if (!navigator.share) {
    deviceSupportsShare.value = false
  }
})

onActivated(() => {
  window.scrollTo(0, 0)
})

const campsite = computed(() => {
  return data.value?.campsite
})

const selectedTag = computed(() => {
  if (route.name === 'campsite-albums-video') {
    return 'video'
  }
  return route.params.tag || 'all'
})

watch(selectedTag, () => {
  logger('selectedTagScrollTo', selectedTag.value)
  window.scrollTo(0, 0)
})

const sorted = computed(() => {
  return tagSorter(
    data.value?.albums,
    data.value?.campsite,
    primaryPhoto.value,
    t,
  )
})

const counts = computed(() => {
  return sorted.value.counts
})

const idToLabelMap = computed(() => {
  return sorted.value.idToLabelMap
})

const computedTags = computed(() => {
  return sorted.value.tags
})

const primaryPhoto = computed(() => {
  if (!campsite.value) {
    return null
  }

  return getPrimaryPhotoFromPitchType(
    campsite.value,
    Array.isArray(route.query.type) ? route.query.type : [route.query.type],
  )
})

function shareCampsiteAlbumsLink() {
  if (navigator.share) {
    navigator
      .share({
        title: `${data.value?.campsite.name} | ${t('Albums')}`,
        url: window.location.href,
      })
      .then(() => console.log('Successful share! 🎉'))
      .catch((err) => console.log(err))
  }
}

const navigationOffScreen = ref(false)
function headerVisibilityChanged(isVisible: boolean) {
  navigationOffScreen.value = !isVisible
}

function backToCampsite() {
  const history = $routerClient.getHistory()
  const csd = history.find((page) => {
    return page.name === 'campsite-detail'
  })

  if (csd) {
    router.go(-1)
  } else {
    void router.push({
      name: 'campsite-slug',
      params: {
        ...route.params,
      },
      query: route.query,
    })
  }
}

const headData = computed(() => {
  return {
    title: `${data.value?.campsite.name} | ${t('Albums')}`,
  }
})

useHead(headData)
</script>

<style scoped>
header {
  background: #fff;
  margin: auto;
  max-width: calc(var(--page-width-max));
  min-height: 4em;
  position: sticky;
  top: -1px;
  width: 100dvw;
  z-index: 1;
}

header::after {
  content: '';
  position: absolute;
  inset: 0 0 0 0;
  opacity: 0;
  transition: opacity 0.3s ease;
  filter: drop-shadow(0 0.3em 0.25em rgba(0, 0, 0, 0.15));
  background: white;
  z-index: -1;
}

header.pinned::after {
  opacity: 1;
}

header:before {
  background: linear-gradient(270deg, #fff 10%, #ffffffe6 20%, #fff0);
  left: auto;
  right: 0;
  width: 2.4em;
  bottom: 0;
  content: '';
  display: block;
  height: 100%;
  pointer-events: none;
  position: absolute;
  top: 0;
  z-index: 1;
}

.back {
  transform: rotate(90deg);
  opacity: 0.8;
  margin: 1px 0.25em 0 0;
}

header h1 {
  font-size: var(--body);
  font-weight: normal;
  margin: 0;
  text-align: left;
  padding-right: 1.5em;
}

header button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.75em 1em 0 1em;
  margin-bottom: 1em;
  border: none;
  box-shadow: none;
}

header button:active {
  background: inherit;
  box-shadow: none;
  transform: translateX(-0.2em);
}

#sharebutton {
  display: block;
  width: 1.4em;
  height: 1.4em;
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  z-index: 2;
  opacity: 0.6;
  cursor: pointer;
}

nav {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 1.1em;
  position: relative;
  scroll-padding: 0 1.1em;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  width: 100%;
  padding-bottom: 0.5em;
  gap: 1em;
}

@supports (scrollbar-width: none) {
  nav {
    scrollbar-width: none;
  }

  .desktop nav {
    scrollbar-width: thin;
  }
}

nav::-webkit-scrollbar {
  display: none;
}

nav a {
  align-self: flex-start;
  color: var(--colour-text-light);
  text-align: center;
  text-decoration: none;
  font-size: var(--small);
  white-space: nowrap;
  position: relative;
  cursor: pointer;
}

nav a.selected {
  color: var(--colour-dark-blue);
  font-weight: bold;
}

nav a.selected:after {
  background: var(--colour-light-green);
  border-radius: 1em;
  bottom: -0.5em;
  content: '';
  height: 4px;
  left: 0;
  position: absolute;
  width: 100%;
}

nav i {
  font-weight: normal;
  font-style: normal;
}

/* desktop & tablet */
#albums.desktop header h1 {
  font-weight: bold;
  font-size: 1.3em;
}

#albums.desktop .back {
  height: 0.7em;
  width: auto;
  opacity: 1;
}

#albums.desktop nav {
  gap: 2em;
}

#albums.desktop nav a {
  font-size: initial;
}

#albums.desktop header {
  max-width: none;
}

#albums.desktop #sharebutton {
  width: 1.8em;
  height: 1.8em;
  top: 0.75em;
  right: 1.5em;
}
</style>
